import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../context/CartContext';
import Button from '../../components/common/Button';
import ConfirmModal from '../../components/common/ConfirmModal';
import { getImageUrl } from '../../utils/imageUrl';


const Cart = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { cartItems, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();
  
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'alert',
    title: '',
    message: '',
    onConfirm: null
  });

  const showModal = (type, title, message, onConfirm = null) => {
    setModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm
    });
  };

  const closeModal = () => {
    setModal({ ...modal, isOpen: false });
  };

  const handleQuantityChange = (item, newQuantity) => {
    if (newQuantity > item.stock) {
      showModal(
        'error',
        t('cart.stockLimit'),
        `${t('cart.onlyAvailable')} ${item.stock} ${t('cart.itemsInStock')}`
      );
      return;
    }
    if (newQuantity < 1) {
      return;
    }
    updateQuantity(item.id, newQuantity);
  };

  const handleRemoveItem = (item) => {
    showModal(
      'confirm',
      t('cart.removeItem'),
      `${t('cart.confirmRemove')} "${item.name}"?`,
      () => {
        removeFromCart(item.id);
        closeModal();
      }
    );
  };

  const handleClearCart = () => {
    showModal(
      'confirm',
      t('cart.clearCart'),
      t('cart.confirmClear'),
      () => {
        clearCart();
        closeModal();
      }
    );
  };

  if (cartItems.length === 0) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: 'var(--light)', paddingTop: '5rem' }}>
        <div className="cart-container">
          <div className="cart-empty">
            <div className="cart-empty-icon">🛒</div>
            <h2>{t('cart.empty')}</h2>
            <Button onClick={() => navigate('/shop')}>{t('cart.continueShopping')}</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--light)', paddingTop: '5rem' }}>
      <div className="cart-container">
        <h1 className="cart-title">{t('cart.title')}</h1>
        
        <div className="cart-content">
          <div className="cart-items">
            {cartItems.map((item) => (
              <div key={item.id} className="cart-item">
                <div className="cart-item-image">
                  {item.primary_image ? (
                    <img
                       src={getImageUrl(item.primary_image)}
                       alt={item.name}
                    />
                  ) : (
                    <div className="no-image">📦</div>
                  )}
                </div>

                <div className="cart-item-details">
                  <h3 className="cart-item-name">{item.name}</h3>
                  <p className="cart-item-category">{item.category_name}</p>
                  <p className="cart-item-price-mobile">₾{parseFloat(item.price).toFixed(2)}</p>
                  <p className="cart-item-stock">
                    {item.stock > 0 ? `${item.stock} ${t('product.inStock')}` : t('product.outOfStock')}
                  </p>
                </div>

                <div className="cart-item-quantity">
                  <button
                    className="quantity-btn-cart"
                    onClick={() => handleQuantityChange(item, item.quantity - 1)}
                    disabled={item.quantity <= 1}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    className="quantity-input-cart"
                    value={item.quantity}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      handleQuantityChange(item, val);
                    }}
                    min="1"
                    max={item.stock}
                  />
                  <button
                    className="quantity-btn-cart"
                    onClick={() => handleQuantityChange(item, item.quantity + 1)}
                    disabled={item.quantity >= item.stock}
                  >
                    +
                  </button>
                </div>

                <div className="cart-item-price">
                  ₾{(item.price * item.quantity).toFixed(2)}
                </div>

                <button
                  className="cart-item-remove"
                  onClick={() => handleRemoveItem(item)}
                  title={t('cart.remove')}
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>

          <div className="cart-summary">
            <h2 className="cart-summary-title">{t('cart.summary')}</h2>
            
            <div className="cart-summary-row">
              <span>{t('cart.subtotal')} ({cartItems.reduce((sum, item) => sum + item.quantity, 0)} {t('cart.items')})</span>
              <span>₾{getCartTotal().toFixed(2)}</span>
            </div>

            <div className="cart-summary-divider"></div>

            <div className="cart-summary-row cart-summary-total">
              <span>{t('cart.total')}</span>
              <span>₾{getCartTotal().toFixed(2)}</span>
            </div>

            <Button 
              variant="primary" 
              block 
              onClick={() => navigate('/checkout')}
              style={{ marginTop: '1.5rem' }}
            >
              {t('cart.checkout')}
            </Button>

            <Button 
              variant="secondary" 
              block 
              onClick={() => navigate('/shop')}
              style={{ marginTop: '1rem' }}
            >
              {t('cart.continueShopping')}
            </Button>

            <button 
              className="clear-cart-btn"
              onClick={handleClearCart}
            >
              {t('filter.clear')}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        onConfirm={modal.onConfirm}
        type={modal.type}
        title={modal.title}
        message={modal.message}
      />
    </div>
  );
};

export default Cart;